import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { API_URL } from '../config.js';
import './AlternativasGraficas.css';

const isYes = (v) =>
  String(v ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .match(/^(si|sí|true|1|ok)$/);

// Detecta "info" en la celda de Puntaje
const isInfoValue = (v) =>
  String(v ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim() === 'info';

// Determina nivel desde la ruta
function inferNivelFromPath() {
  const p =
    (
      typeof window !== 'undefined'
        ? window.location.pathname
        : ''
    ).toLowerCase();

  if (p.includes('repaso')) {
    return 'Repaso';
  }

  if (
    p.includes('basico') ||
    p.includes('básico')
  ) {
    return 'Basico';
  }

  if (p.includes('medio')) {
    return 'Medio';
  }

  if (p.includes('avanz')) {
    return 'Avanzado';
  }

  return 'Desconocido';
}

// Registrar la respuesta seleccionada.
// Se mantiene fire-and-forget para no bloquear la navegación,
// pero ahora valida errores HTTP.
function registrarRespuesta({
  pregunta,
  letra,
  es_correcta
}) {
  try {
    const session_id =
      localStorage.getItem('sesion') ||
      `SESS_${Date.now()}`;

    localStorage.setItem(
      'sesion',
      session_id
    );

    const nombreUsuario =
      localStorage.getItem('usuario') ||
      'Jugador';

    const nivel =
      inferNivelFromPath();

    const device =
      /Mobi/.test(navigator.userAgent)
        ? 'mobile'
        : 'desktop';

    fetch(`${API_URL}/api/answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        session_id,
        nombreUsuario,
        nivel,
        pregunta_id: pregunta?.ID,
        tipo_pregunta:
          'alternativas_graficas',
        respuesta_usuario: letra,
        es_correcta:
          es_correcta ?? null,
        device,
        ts: Date.now()
      })
    })
      .then(async (response) => {
        if (response.ok) {
          return;
        }

        const detalle =
          await response
            .text()
            .catch(() => '');

        console.error(
          '❌ Error HTTP registrando respuesta ' +
          '(Alternativas):',
          response.status,
          detalle
        );
      })
      .catch((error) => {
        console.error(
          '❌ Error de red registrando respuesta ' +
          '(Alternativas):',
          error
        );
      });
  } catch (error) {
    console.error(
      '❌ Error registrando respuesta ' +
      '(Alternativas):',
      error
    );
  }
}

export default function AlternativasGraficas({
  pregunta,
  onNext,
  onScore,
  onLoseLife,
  reemplazarNombre,
  resolveGeneral,
  getLives,
  noLivesId = '1000'
}) {
  const [sel, setSel] =
    useState(null);

  const [zoom, setZoom] =
    useState(false);

  const [showFb, setShowFb] =
    useState(false);

  const [esCorrecto, setEsCorrecto] =
    useState(null);

  const [siguienteSeleccionado, setSiguienteSeleccionado] =
    useState(null);

  // Bloqueo inmediato. Evita que dos clics entren
  // antes de que React alcance a actualizar el estado.
  const procesandoRef =
    useRef(false);

  // Conserva siempre la versión más reciente de onNext.
  const onNextRef =
    useRef(onNext);

  // Guarda el temporizador activo para poder cancelarlo.
  const timerRef =
    useRef(null);

  useEffect(() => {
    onNextRef.current =
      onNext;
  }, [onNext]);

  const limpiarTimer = () => {
    if (timerRef.current) {
      clearTimeout(
        timerRef.current
      );

      timerRef.current =
        null;
    }
  };

  // Limpia estado al cambiar de pregunta.
  // Esto evita que una selección o temporizador anterior
  // afecte la siguiente pregunta.
  useEffect(() => {
    limpiarTimer();

    procesandoRef.current =
      false;

    setSel(null);
    setZoom(false);
    setShowFb(false);
    setEsCorrecto(null);
    setSiguienteSeleccionado(null);

    return () => {
      limpiarTimer();
    };
  }, [pregunta?.ID]);

  const imgSrc = useMemo(
    () =>
      resolveGeneral?.(
        pregunta?.Imagen ||
        pregunta?.ImagenGral
      ) || null,
    [
      pregunta,
      resolveGeneral
    ]
  );

  const esperarOK = useMemo(
    () =>
      !!isYes(
        pregunta?.EsperarOK
      ),
    [pregunta]
  );

  const avanzarSeguro = (next) => {
    limpiarTimer();

    const siguienteId =
      String(next ?? '').trim();

    const preguntaActualId =
      String(pregunta?.ID ?? '').trim();

    /*
    * En Repaso, una alternativa puede no avanzar
    * o puede apuntar nuevamente a la pregunta actual.
    * En ambos casos se libera la selección para permitir
    * que el usuario pruebe otra alternativa.
    */
    if (
      !siguienteId ||
      siguienteId === preguntaActualId
    ) {
      procesandoRef.current =
        false;

      setSel(null);
      setShowFb(false);
      setEsCorrecto(null);
      setSiguienteSeleccionado(null);

      return;
    }

    /*
    * Solo se mantiene el bloqueo cuando realmente
    * corresponde cambiar a otra pregunta.
    */
    if (
      typeof onNextRef.current ===
      'function'
    ) {
      onNextRef.current(
        siguienteId
      );
    } else {
      procesandoRef.current =
        false;

      setSel(null);
      setShowFb(false);
      setEsCorrecto(null);
      setSiguienteSeleccionado(null);
    }
  };

  const pick = (L) => {
    if (
      procesandoRef.current ||
      showFb
    ) {
      return;
    }

    procesandoRef.current =
      true;

    setSel(L);

    const rawPuntaje =
      pregunta?.[`Puntaje${L}`];

    const info =
      isInfoValue(rawPuntaje);

    const fbText =
      String(
        pregunta?.[`Feedback${L}`] ||
        ''
      ).trim();

    const next =
      pregunta?.[`SiguienteID${L}`];

    setSiguienteSeleccionado(
      next || null
    );

    // Rama INFO:
    // registra, muestra feedback y avanza,
    // sin modificar puntaje ni vidas.
    if (info) {
      registrarRespuesta({
        pregunta,
        letra: L,
        es_correcta: 'Info'
      });

      setEsCorrecto(null);

      if (fbText) {
        setShowFb(true);
      }

      if (!fbText) {
        avanzarSeguro(next);
      } else if (!esperarOK) {
        timerRef.current =
          setTimeout(() => {
            setShowFb(false);
            avanzarSeguro(next);
          }, 2500);
      }

      return;
    }

    // Rama numérica
    const delta =
      parseInt(
        rawPuntaje,
        10
      );

    const tieneNumero =
      !Number.isNaN(delta);

    const correcto =
      tieneNumero
        ? delta > 0
        : false;

    setEsCorrecto(
      correcto
    );

    registrarRespuesta({
      pregunta,
      letra: L,
      es_correcta:
        correcto
          ? true
          : false
    });

    // NivelBasico mantiene el puntaje inmediato mediante puntajeRef.
    if (
      tieneNumero &&
      typeof onScore ===
        'function'
    ) {
      onScore(delta);
    }

    if (
      tieneNumero &&
      delta <= 0
    ) {
      /*
       * Se obtiene la cantidad de vidas ANTES de ejecutar
       * setVidas en el padre. Luego se calcula localmente
       * el valor restante.
       *
       * Esto evita depender del retorno asincrónico
       * de setVidas().
       */
      let vidasAntes =
        null;

      if (
        typeof getLives ===
        'function'
      ) {
        const actuales =
          Number(
            getLives()
          );

        if (
          Number.isFinite(
            actuales
          )
        ) {
          vidasAntes =
            actuales;
        }
      }

      let retornoPadre =
        null;

      if (
        typeof onLoseLife ===
        'function'
      ) {
        retornoPadre =
          onLoseLife();
      }

      let remaining =
        null;

      if (
        Number.isFinite(
          retornoPadre
        )
      ) {
        remaining =
          retornoPadre;
      } else if (
        Number.isFinite(
          vidasAntes
        )
      ) {
        remaining =
          Math.max(
            vidasAntes - 1,
            0
          );
      }

      if (
        Number.isFinite(
          remaining
        ) &&
        remaining <= 0
      ) {
        avanzarSeguro(
          noLivesId
        );

        return;
      }
    }

    if (fbText) {
      setShowFb(true);
    }

    if (correcto) {
      if (!fbText) {
        avanzarSeguro(next);
      } else if (!esperarOK) {
        timerRef.current =
          setTimeout(() => {
            setShowFb(false);
            avanzarSeguro(next);
          }, 2500);
      }

      return;
    }

    // Respuesta incorrecta
    if (
      fbText &&
      !esperarOK
    ) {
      timerRef.current =
        setTimeout(() => {
          setShowFb(false);
          setSel(null);
          setEsCorrecto(null);
          setSiguienteSeleccionado(null);

          procesandoRef.current =
            false;

          timerRef.current =
            null;
        }, 1300);
    } else if (!fbText) {
      setSel(null);
      setEsCorrecto(null);
      setSiguienteSeleccionado(null);

      procesandoRef.current =
        false;
    }
  };

  const feedbackHtml =
    useMemo(() => {
      if (!sel) {
        return '';
      }

      const raw =
        pregunta?.[`Feedback${sel}`] ||
        '';

      return reemplazarNombre
        ? reemplazarNombre(raw)
        : raw;
    }, [
      sel,
      pregunta,
      reemplazarNombre
    ]);

  const cerrarFeedback = () => {
    limpiarTimer();

    setShowFb(false);

    if (!esCorrecto) {
      setSel(null);
      setEsCorrecto(null);
      setSiguienteSeleccionado(null);

      procesandoRef.current =
        false;
    }
  };

  const continuarOK = () => {
    const infoSel =
      isInfoValue(
        pregunta?.[`Puntaje${sel}`]
      );

    setShowFb(false);

    // Avanza si fue correcto o si era un paso INFO.
    if (
      esCorrecto ||
      infoSel
    ) {
      avanzarSeguro(
        siguienteSeleccionado
      );

      return;
    }

    // Incorrecto: cierra feedback y se mantiene
    // en la misma pregunta.
    setSel(null);
    setEsCorrecto(null);
    setSiguienteSeleccionado(null);

    procesandoRef.current =
      false;
  };

  return (
    <div className="altg-wrap">
      <div className="altg-contenido">
        {imgSrc && (
          <div
            className="altg-imagen-centro"
            style={{
              minHeight: 0
            }}
          >
            <img
              src={imgSrc}
              alt="Imagen de la pregunta"
              onClick={() =>
                setZoom(true)
              }
            />
          </div>
        )}

        <div
          className="altg-grid"
          aria-disabled={
            showFb
          }
        >
          <div className="altg-col">
            <button
              className={
                `altg-opcion color-a ${
                  sel === 'A'
                    ? 'seleccionada'
                    : ''
                }`
              }
              onClick={() =>
                pick('A')
              }
              disabled={
                showFb
              }
            >
              {pregunta?.OpcionA}
            </button>

            <button
              className={
                `altg-opcion color-c ${
                  sel === 'C'
                    ? 'seleccionada'
                    : ''
                }`
              }
              onClick={() =>
                pick('C')
              }
              disabled={
                showFb
              }
            >
              {pregunta?.OpcionC}
            </button>
          </div>

          <div className="altg-col">
            <button
              className={
                `altg-opcion color-b ${
                  sel === 'B'
                    ? 'seleccionada'
                    : ''
                }`
              }
              onClick={() =>
                pick('B')
              }
              disabled={
                showFb
              }
            >
              {pregunta?.OpcionB}
            </button>

            <button
              className={
                `altg-opcion color-d ${
                  sel === 'D'
                    ? 'seleccionada'
                    : ''
                }`
              }
              onClick={() =>
                pick('D')
              }
              disabled={
                showFb
              }
            >
              {pregunta?.OpcionD}
            </button>
          </div>
        </div>
      </div>

      {showFb &&
        feedbackHtml?.trim() && (
          <div
            className="altg-fb-modal"
            onClick={() =>
              esperarOK
                ? null
                : (
                    esCorrecto
                      ? null
                      : cerrarFeedback()
                  )
            }
          >
            <div
              className="altg-fb-card"
              onClick={(e) =>
                e.stopPropagation()
              }
            >
              <p
                dangerouslySetInnerHTML={{
                  __html:
                    feedbackHtml
                }}
              />

              {esperarOK && (
                <button
                  className="altg-btn-ok"
                  onClick={
                    continuarOK
                  }
                >
                  {
                    esCorrecto ||
                    isInfoValue(
                      pregunta?.[
                        `Puntaje${sel}`
                      ]
                    )
                      ? 'Continuar'
                      : 'OK'
                  }
                </button>
              )}
            </div>
          </div>
        )}

      {zoom && imgSrc && (
        <div
          className="altg-modal"
          onClick={() =>
            setZoom(false)
          }
        >
          <img
            src={imgSrc}
            alt="Imagen ampliada"
          />

          <button
            className="altg-cerrar"
            onClick={() =>
              setZoom(false)
            }
          >
            ✖
          </button>
        </div>
      )}
    </div>
  );
}